<template>
    <div class="apprentice-simple-card">
        <design2021-apprentice-data-table
            class="period-modules-overview-table"
            :items="students"
            :fields="fields"
            :filter-function="advancedSearch"
            :show-empty="true"
            :download-excel-settings="downloadExcelSettings">
            <template #options>
                <div class="button-group">
                    <b-form-select
                        class="apprentice-primary-select module-period-filter"
                        v-model="selectedModulePeriod"
                        :options="periods"
                        value-field="id"
                        text-field="title"
                        :disabled="periods.length === 1"
                        @change="showSelectedPeriod">
                    </b-form-select>

                    <b-button
                        class="btn-unsubscribed-students"
                        variant="primary"
                        @click="showUnsubscribedStudents">
                        {{ $t('modules.students_without_module') }}
                    </b-button>
                </div>
            </template>

            <template #cell(modules)="row">
                <div class="modules">
                    <span
                        class="module-name"
                        v-for="module of row.item.modules"
                        :key="`${row.item.full_name}-${module.id}`">
                        {{ module.title }}
                    </span>
                </div>
            </template>

            <template #empty>
                <div class="not-found with-header modules-list-empty">
                    <div class="icon">
                        <i class="fas fa-exclamation-circle"></i>
                    </div>
                    <div class="alert-notice">{{ $t('modules.no_students_found') }}</div>
                </div>
            </template>

            <template #emptyfiltered>
                <div class="not-found with-header modules-list-empty">
                    <div class="icon">
                        <i class="fas fa-exclamation-circle"></i>
                    </div>
                    <div class="alert-notice">{{ $t('modules.no_results_found') }}</div>
                </div>
            </template>
        </design2021-apprentice-data-table>
        <design-2021-module-unsubscribed-students-modal-component
            :ref="'unsubscribed-students-modal'"
            :unsubscribed-students-in="unsubscribedStudentsIn">
        </design-2021-module-unsubscribed-students-modal-component>
    </div>
</template>

<script>
import Design2021ApprenticeDataTable from '../common/design-2021-apprentice-data-table.vue';

export default {
    name: 'design-2021-module-period-overview-students-component.vue',
    components: { Design2021ApprenticeDataTable },
    props: {
        periodIn: {
            type: Object,
            required: true,
            default: () => {},
        },
        modulePeriodIn: {
            type: Array,
            required: true,
            default: () => [],
        },
        unsubscribedStudentsIn: {
            type: Array,
            required: true,
        },
    },

    data() {
        return {
            fields: [
                {
                    key: 'full_name',
                    label: this.$t('modules.student'),
                },
                {
                    key: 'modules',
                    label: 'Module',
                },
            ],
            downloadExcelSettings: [
                {
                    name: this.$t('modules.student'),
                    value: 'full_name',
                },
                {
                    name: 'Module',
                    value: (row) => {
                        return row.modules.map((module) => module.title).join(',');
                    },
                },
            ],
            selectedModulePeriod: null,
        };
    },

    mounted() {
        if (this.periodIn) {
            this.selectedModulePeriod = this.periodIn.id;
        }
    },

    computed: {
        students() {
            if (!this.periodIn) {
                return [];
            }

            let allStudents = [];

            for (const module_date of this.periodIn.modules_dates) {
                for (const module_user of module_date.module_users) {
                    let newStudent = module_user.user;
                    newStudent.module_date_id = module_user.module_date_id;
                    newStudent.module_id = module_user.module_id;
                    newStudent.module_user_id = module_user.id;

                    if (
                        allStudents.length === 0 ||
                        allStudents.map((student) => student.id).indexOf(newStudent.id) === -1
                    ) {
                        allStudents.push(newStudent);
                    }
                }
            }

            allStudents = allStudents.map((student) => {
                let modules = [];

                for (const module_date of this.periodIn.modules_dates) {
                    let module = {
                        id: module_date.module_id,
                        module_period_id: module_date.module_period_id,
                        school_id: module_date.module.school_id,
                        title: module_date.module.title,
                    };

                    if (module_date.module_users.map((module_user) => module_user.user_id).indexOf(student.id) !== -1) {
                        if (modules.length === 0 || modules.map((module) => module.id).indexOf(module.id) === -1) {
                            modules.push(module);
                        }
                    }
                }

                student.modules = modules;
                return student;
            });

            return allStudents;
        },

        periods() {
            let availablePeriods = [
                {
                    key: null,
                    title: this.$t('modules.choose_period'),
                    disabled: true,
                },
            ];

            if (this.modulePeriodIn.length > 0) {
                availablePeriods.push(...this.modulePeriodIn);
            }

            return availablePeriods;
        },
    },

    methods: {
        showUnsubscribedStudents() {
            this.$refs['unsubscribed-students-modal'].show();
        },
        showSelectedPeriod(periodId) {
            window.location.href = this.routes.managementModulesPeriod(periodId);
        },

        advancedSearch(row, filter) {
            const cleanFilter = this.$options.filters.normalize_string(filter);
            const name = this.$options.filters.normalize_string(row.full_name);

            if (name.indexOf(cleanFilter) !== -1) {
                return true;
            }

            for (const module of row.modules) {
                const module_name = this.$options.filters.normalize_string(module.title);
                if (module_name.indexOf(cleanFilter) !== -1) {
                    return true;
                }
            }

            return false;
        },
    },
};
</script>

<style scoped lang="scss">
.modules {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    column-gap: map-get($grid-gutter-spacing, 'ten');
    row-gap: map-get($grid-gutter-spacing, 'ten');

    .module-name {
        position: relative;
        color: $text-muted-color;
        font-weight: $font-weight-medium;

        &:not(:last-child) {
            &:after {
                content: ', ';
                position: absolute;
            }
        }
    }
}

.btn-unsubscribed-students {
    margin-left: map-get($grid-gutter-spacing, 'fifteen');
}
.period-modules-overview-table {
    ::v-deep .options {
        column-gap: map-get($grid-gutter-spacing, 'ten');

        .btn-export-as-excel {
            font-size: 16px;
            line-height: 1.5;
        }
    }
}
.module-period-filter {
    width: 250px;
}
select {
    margin: 0;
}
</style>
